'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { LuChevronDown } from 'react-icons/lu'

import { cn } from '@/core/utils/common'

import type { Route } from 'next'
import type { ReactNode } from 'react'

type NavMenuProps = {
  label: string
  href: Route
  children: ReactNode
}

export default function NavMenu({ label, href, children }: NavMenuProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLLIElement>(null)

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open])

  return (
    <li
      ref={rootRef}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocusCapture={(event) => {
        if (event.target.matches(':focus-visible')) setOpen(true)
      }}
      onBlurCapture={(event) => {
        if (!rootRef.current?.contains(event.relatedTarget)) setOpen(false)
      }}
      onClickCapture={(event) => {
        if ((event.target as HTMLElement).closest('a')) setOpen(false)
      }}
    >
      <Link
        href={href}
        className={cn(
          'inline-flex h-14 items-center gap-1 whitespace-nowrap text-c16 font-semibold transition-colors hover:text-point',
          open && 'text-point'
        )}
      >
        {label}
        <LuChevronDown
          aria-hidden="true"
          className={cn(
            'size-4 text-description transition-transform duration-200',
            open && 'rotate-180'
          )}
        />
      </Link>
      <div
        className={cn(
          'absolute inset-x-0 top-full z-40 border-y border-border bg-background transition-[opacity,transform,visibility] duration-150',
          open
            ? 'visible translate-y-0 opacity-100'
            : 'invisible -translate-y-1 opacity-0'
        )}
      >
        {children}
      </div>
    </li>
  )
}
